import { AIWorkflowConfig, WorkflowStep, DecisionNode, ActionStepData, TriggerStepData } from './types';
import { ApiWorkflow, ApiWorkflowStep, ExecutionStatus } from 'api/types/aiWorkflowTypes';
import { ACTION_TYPES } from './constants';

export const TRIGGER_DISPLAY_NAMES: Record<string, string> = {
  reimbursement_created: 'Reimbursement Created',
  leave_created: 'Leave Created',
};

export const TRIGGER_DESCRIPTIONS: Record<string, string> = {
  reimbursement_created: 'Triggered when a new reimbursement is submitted',
  leave_created: 'Triggered when a new leave request is submitted',
};


export const convertApiStepToAction = (step: ApiWorkflowStep, order: number): WorkflowStep => ({
  id: String(step.id),
  type: 'action',
  order,
  isConfigured: true,
  data: {
    code: step.action_type,
    name: step.action,
    group: '',
    config: step.configuration || undefined,
  } as ActionStepData,
});

export const convertApiWorkflowToListItem = (workflow: ApiWorkflow): AIWorkflowConfig => {
  const actionSteps = workflow.steps?.filter(step => step.action_type !== ACTION_TYPES.AI_DECISION) || [];
  const decisionStep = workflow.steps?.find(step => step.action_type === ACTION_TYPES.AI_DECISION);

  const triggerStep: WorkflowStep | null = workflow.trigger ? {
    id: `trigger-${workflow.id}`,
    type: 'trigger',
    order: 1,
    isConfigured: true,
    data: {
      code: workflow.trigger,
      name: TRIGGER_DISPLAY_NAMES[workflow.trigger] || workflow.trigger,
      description: TRIGGER_DESCRIPTIONS[workflow.trigger] || '',
      config: {
        triggerScope: workflow.metadata?.triggerScope || 'all_users',
        employeeList: workflow.metadata?.employeeList || [],
        employeeNames: workflow.metadata?.employeeNames || [],
      },
    } as TriggerStepData,
  } : null;

  return {
    id: workflow.id,
    name: workflow.name || 'Untitled flow',
    isEnabled: workflow.is_active,
    triggerCode: workflow.trigger,
    triggerScope: workflow.metadata?.triggerScope || 'all_users',
    employeeList: workflow.metadata?.employeeList || [],
    employeeNames: workflow.metadata?.employeeNames || [],
    trigger: triggerStep,
    decision: decisionStep ? {
      id: String(decisionStep.id),
      type: 'decision',
      order: 2,
      isConfigured: true,
      data: {
        id: String(decisionStep.id),
        prompt: decisionStep.configuration?.userWorkflowDecisionPrompt || '',
        isEnabled: true,
      } as DecisionNode,
    } : null,
    actions: actionSteps.map((step, index) => convertApiStepToAction(step, index + (decisionStep ? 3 : 2))),
    actionsCount: actionSteps.length,
    createdAt: workflow.created_at,
    updatedAt: workflow.updated_at,
  };
};

export const formatDate = (dateString: string | null): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const formatEntityType = (type: string): string => {
  return type
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

type BoxBackgroundColor = 
  | 'surface.background.primary.subtle'
  | 'surface.background.gray.moderate'
  | 'surface.background.gray.subtle';

export const getDecisionBackgroundColor = (decision: string): BoxBackgroundColor => {
  switch (decision) {
    case 'APPROVED':
      return 'surface.background.primary.subtle';
    case 'REJECTED':
    case 'MANUAL_REVIEW':
      return 'surface.background.gray.moderate';
    default:
      return 'surface.background.gray.subtle';
  }
};
